// A utility for carrying state from one page location to the next navigation, without affecting the URL the user sees.
import React, {useState, useEffect} from 'react';

import {withRouter} from 'react-router-dom';

const registeredFlashQueryParams = {};

// Combines many errors into one, with a single joined message topped with a `topMessage`.
const combineErrors = (topMessage, errors) => {
  const messages = errors.map(({message, stack}, idx) => {
    return `---Error number ${idx + 1} of ${errors.length}---\n${message}\n${stack}`;
  });
  const combinedMessage = [topMessage, ...messages].join('\n');
  return new Error(combinedMessage);
};

// Attempts to convert raw query string values by trying `null`, `undefined`, Boolean, and Number. Falls back on returning the value as-is (a string).
const defaultDeserializer = (serializedValue) => {
  if (serializedValue === 'null') return null;
  if (serializedValue === 'undefined') return undefined;
  if (serializedValue === 'true') return true;
  if (serializedValue === 'false') return false;
  const attemptedNumber = Number(serializedValue);
  if (!Object.is(NaN, attemptedNumber)) return attemptedNumber;
  return serializedValue;
};

// Registers an auto-generated flash query param key so that the flash query param provider knows to listen for it.
let count = 0;
export const createFlashQueryParam = (deserialize = defaultDeserializer) => {
  const id = ++count;
  const key = `_flash_query_param_${id}`;
  registeredFlashQueryParams[key] = {
    deserialize,
    handlers: new Set()
  };
  return key;
};

// Finds all registered flash query params, stores their values, then removes them from the URL.
const runUpdates = ({history, search = history.location.search} = {}) => {
  const parsedQueryString = new URLSearchParams(search.slice(1));
  const handlerErrors = [];
  let queryStringHasChanged = false;
  for (const key of Object.keys(registeredFlashQueryParams)) {
    if (!parsedQueryString.has(key)) continue;
    const entry = registeredFlashQueryParams[key];
    const {deserialize, handlers} = entry;
    const value = entry.mostRecentValue = deserialize(parsedQueryString.get(key));
    parsedQueryString.delete(key);
    queryStringHasChanged = true;
    for (const handleChange of handlers) {
      // It is important to defer any errors until afterwards, so that each handler can be called independently of the others.
      try {
        handleChange(value);
      } catch (error) {
        handlerErrors.push(error);
      }
    }
  }
  if (queryStringHasChanged) {
    // `.replace` here is IMPORTANT, `.push` would trigger `runUpdates`, causing an infinite loop.
    history.replace({search: parsedQueryString.toString()});
  }
  if (handlerErrors.length === 0) return;
  throw combineErrors(
    `Encountered ${handlerErrors.length} errors attempting to update flash query param values`,
    handlerErrors
  );
};

// A hook that starts the listen-store-and-remove for any registered flash query param upon any history push (navigation change).
export const useFlashQueryParamProvider = ({history}) => {
  useEffect(() => {
    runUpdates({history});
    return history.listen(({search}, action) => {
      if (action !== 'PUSH') return;
      runUpdates({history, search});
    });
  }, [history]);
};

// A component version of `useFlashQueryParamProvider`.
export const FlashQueryParamProvider = withRouter(({children, history}) => {
  useFlashQueryParamProvider({history});
  return (
    <>
      {children}
    </>
  );
});

// A hook for updating a component with a flash query param's value any time it appears
export const useFlashQueryParam = (key) => {
  const entry = registeredFlashQueryParams[key];
  if (!entry) {
    throw Error(`Cannot \`useFlashQueryParam\` for a key that has not been registered with \`createFlashQueryParam\`, namely: '${key}'`);
  }
  // The default value of `entry.mostRecentValue` is important so that this hook can provide the correct value, even if it is invoked *after* the navigation change has completed.
  const [value, setValue] = useState(entry.mostRecentValue);
  useEffect(() => {
    const handleChange = setValue;
    const {handlers} = entry;
    handlers.add(handleChange);
    return () => handlers.delete(handleChange);
  }, [key]);
  return value;
};

// A higher-order-component version of `useFlashQueryParam`. It decorates a component with props that get mapped from flash query param keys to flash query param values.
export const withFlashQueryParams = (propsToParamNames) => (InnerComponent) => {
  const ComponentWithFlashQueryParamProps = (props) => {
    const flashQueryParamProps = {};
    for (const propName of Object.keys(propsToParamNames)) {
      const paramName = propsToParamNames[propName];
      flashQueryParamProps[propName] = useFlashQueryParam(paramName);
    }
    return (
      <InnerComponent
        {...props}
        {...flashQueryParamProps}
      />
    );
  };
  ComponentWithFlashQueryParamProps.propTypes = InnerComponent.propTypes;
  return ComponentWithFlashQueryParamProps;
};
